// @flow

import React, { useCallback, useState } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { FormattedMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';

import StandardButton from '../../Buttons/Standard';
import { GET_MY_TALKDESK_EMAIL } from '../../../containers/Profile/queries';
import { SET_MY_TALKDESK_EMAIL } from '../../../containers/Profile/mutations';
import type { TalkdeskLinkError } from './types';

const TalkdeskEmailLink = () => {
  const [selectedEmail, setSelectedEmail] = useState<?string>(null);
  const [linkError, setLinkError] = useState<?TalkdeskLinkError>(null);
  const { data, loading, error } = useQuery(GET_MY_TALKDESK_EMAIL, {
    fetchPolicy: 'network-only',
  });
  const [setMyTalkdeskEmail, { loading: saving }] = useMutation(SET_MY_TALKDESK_EMAIL, {
    refetchQueries: [{ query: GET_MY_TALKDESK_EMAIL }],
    awaitRefetchQueries: true,
  });
  const linkedEmail = data?.myTalkdeskEmail;
  const options: string[] = data?.myTalkdeskEmailOptions || [];
  const emailToLink = selectedEmail || options[0];

  const handleChange = useCallback(
    async (talkdeskEmail: ?string) => {
      setLinkError(null);
      try {
        await setMyTalkdeskEmail({ variables: { talkdeskEmail } });
      } catch (mutationError) {
        const graphQLErrors = mutationError.graphQLErrors || [];
        const validationError = graphQLErrors.find(
          (graphQLError) =>
            graphQLError.extensions?.state?.errorCode === 'TALKDESK_EMAIL_ALREADY_LINKED' ||
            graphQLError.extensions?.state?.talkdeskEmail,
        );
        const state = validationError?.extensions?.state;
        const emailErrors = state?.talkdeskEmail || [];
        if (
          state?.errorCode === 'TALKDESK_EMAIL_ALREADY_LINKED' ||
          emailErrors.includes('This Talkdesk email is already linked to another user')
        ) {
          setLinkError({
            messageId: state?.linkedSceneEmail
              ? 'app.profile.talkdesk.conflict-owner'
              : 'app.profile.talkdesk.conflict',
            talkdeskEmail: state?.linkedTalkdeskEmail || talkdeskEmail,
            sceneEmail: state?.linkedSceneEmail,
          });
        } else if (state?.talkdeskEmail) {
          setLinkError({ messageId: 'app.profile.talkdesk.invalid-email' });
        } else {
          setLinkError({ messageId: 'app.profile.talkdesk.error' });
        }
      }
    },
    [setMyTalkdeskEmail],
  );
  const handleLink = useCallback(() => handleChange(emailToLink), [handleChange, emailToLink]);
  const handleUnlink = useCallback(() => handleChange(null), [handleChange]);
  const handleSelection = useCallback((event) => {
    setSelectedEmail(event.target.value);
    setLinkError(null);
  }, []);

  return (
    <Box component="fieldset" my={2}>
      <Typography component="legend" id="talkdesk-email-title">
        <FormattedMessage id="app.profile.talkdesk.title" />
      </Typography>
      {loading && (
        <Typography>
          <FormattedMessage id="app.profile.talkdesk.loading" />
        </Typography>
      )}
      {error && (
        <Typography role="alert">
          <FormattedMessage id="app.profile.talkdesk.load-error" />
        </Typography>
      )}
      {linkError && (
        <Typography role="alert" color="error">
          <FormattedMessage
            id={linkError.messageId}
            values={{ email: linkError.talkdeskEmail, sceneEmail: linkError.sceneEmail }}
          />
        </Typography>
      )}
      {!loading && !error && linkedEmail && (
        <>
          <Typography>
            <FormattedMessage id="app.profile.talkdesk.linked-email" values={{ email: linkedEmail }} />
          </Typography>
          <Box mt={1}>
            <StandardButton type="button" variant="tertiary" disabled={saving} onClick={handleUnlink}>
              <FormattedMessage id="app.profile.talkdesk.unlink" />
            </StandardButton>
          </Box>
        </>
      )}
      {!loading && !error && !linkedEmail && (
        <>
          <Typography>
            <FormattedMessage id="app.profile.talkdesk.not-linked" />
          </Typography>
          <Typography>
            <FormattedMessage id="app.profile.talkdesk.description" />
          </Typography>
          {options.length > 1 ? (
            <RadioGroup
              aria-labelledby="talkdesk-email-title"
              name="talkdeskEmail"
              row
              value={emailToLink}
              onChange={handleSelection}
            >
              {options.map((option) => (
                <FormControlLabel key={option} value={option} control={<Radio />} label={option} disabled={saving} />
              ))}
            </RadioGroup>
          ) : (
            <Typography>{emailToLink}</Typography>
          )}
          <Box mt={1}>
            <StandardButton type="button" variant="secondary" disabled={saving || !emailToLink} onClick={handleLink}>
              <FormattedMessage id="app.profile.talkdesk.link" />
            </StandardButton>
          </Box>
        </>
      )}
    </Box>
  );
};

export default TalkdeskEmailLink;
